import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Pick } from "@/pages/gerencia/Sales";

const EMPTY = { name: "", kind: "contrato", body: "", active: true };
const KINDS = [{ id: "contrato", name: "Contrato" }, { id: "anexo", name: "Anexo" }];

const TemplateDialog = ({ edit, setEdit, variables, onSaved }) => {
  const insert = (v) => setEdit({ ...edit, body: `${edit.body}{{${v}}}` });
  const save = async () => {
    try { edit.id ? await api.patch(`/rrhh/contract-templates/${edit.id}`, edit) : await api.post("/rrhh/contract-templates", edit); toast.success("Plantilla guardada"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{edit?.id ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>
        {edit && <div className="space-y-4">
          <div className="grid sm:grid-cols-[1fr_180px] gap-3">
            <Field label="Nombre *"><Input data-testid="template-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Tipo *"><Pick testid="template-kind-select" disabled={!!edit.docx_file} value={edit.kind} onChange={(v) => setEdit({ ...edit, kind: v })} options={KINDS} /></Field>
          </div>
          {!edit.docx_file && <div>
            <div className="text-[13px] font-semibold mb-1.5">Variables disponibles (clic para insertar)</div>
            <div className="flex flex-wrap gap-1.5">{Object.entries(variables).map(([k, l]) => (
              <button key={k} type="button" title={l} data-testid={`template-var-${k}`} onClick={() => insert(k)} className="rounded-full border border-[#bfe6a8] bg-[#f6fbf2] px-2.5 py-0.5 font-mono text-[11px] text-[#1c6e00] hover:bg-[#e9f7e1]">{`{{${k}}}`}</button>))}</div>
            <p className="text-xs text-[#5b6656] mt-1.5">También puede usar variables propias, por ejemplo {"{{jornada}}"}: se pedirán al generar el contrato.</p>
          </div>}
          <Field label="Texto del documento *" hint={edit.docx_file ? "Plantilla en formato Word: el texto se toma del archivo original y no se edita aquí." : ""}><Textarea data-testid="template-body-input" rows={14} className="font-mono text-[13px]" readOnly={!!edit.docx_file} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="template-active-switch" checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} />Activa</label>
        </div>}
        <DialogFooter><Button data-testid="template-save-btn" className="rounded-full" onClick={save}>Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ContractTemplates() {
  const { can } = useAuth();
  const canEdit = can("rrhh.plantillas.gestionar");
  const [data, setData] = useState({ items: [], variables: {} });
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/rrhh/contract-templates").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="contract-templates-page">
      <PageHeader eyebrow="RR.HH." title="Plantillas de contratos" subtitle="Textos con variables que se completan automáticamente con los datos del trabajador, la OT y la empresa."
        actions={canEdit && <Button data-testid="create-template-btn" className="rounded-full" onClick={() => setEdit({ ...EMPTY })}><Plus size={16} className="mr-1" />Nueva plantilla</Button>} />
      <div className="syc-card overflow-hidden">
        {data.items.length === 0 ? <EmptyState title="Sin plantillas" text="Cree una plantilla de contrato o de anexo para empezar." /> : data.items.map((t) => (
          <div key={t.id} data-testid={`template-row-${t.id}`} className={`flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#f0f2ee] text-sm ${t.active ? "" : "opacity-50"}`}>
            <button className="font-semibold flex-1 min-w-[180px] text-left hover:underline" onClick={() => canEdit && setEdit({ id: t.id, name: t.name, kind: t.kind, body: t.body, active: t.active, docx_file: t.docx_file })} data-testid={`template-edit-${t.id}`}>{t.name}</button>
            {t.docx_file && <StatusBadge testid={`template-word-${t.id}`} status="activo" label="Formato Word" />}
            <StatusBadge status={t.kind === "anexo" ? "pendiente" : "info"} label={t.kind === "anexo" ? "Anexo" : "Contrato"} />
            <span className="text-xs text-[#5b6656]">v{t.version} · {t.variables.length} variables{t.custom_vars.length ? ` (${t.custom_vars.length} propias)` : ""}</span>
          </div>
        ))}
      </div>
      <TemplateDialog edit={edit} setEdit={setEdit} variables={data.variables} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
